import { useEffect, useState, type CSSProperties } from 'react'
import { Toaster } from 'sonner'
import { useThemeStore } from '@/stores/themeStore'

type Resolved = 'light' | 'dark'

function resolve(theme: 'light' | 'dark' | 'system'): Resolved {
  if (theme !== 'system') return theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * App-wide toast host, styled like the Unnati ERP: a compact white card at
 * the top centre with a coloured status icon and a round close button on its
 * top-left corner, following the app's light/dark mode.
 *
 * Toasts close on their own after 3 s. Sonner pauses that timer while the
 * pointer is over the toast stack (and while the tab is hidden), so a message
 * being read never disappears under the cursor; it resumes on mouse-out.
 */
export function ThemedToaster() {
  const theme = useThemeStore((s) => s.theme)
  const [resolved, setResolved] = useState<Resolved>(() => resolve(theme))

  useEffect(() => {
    setResolved(resolve(theme))
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setResolved(resolve('system'))
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  return (
    <Toaster
      position="top-center"
      offset={28}
      theme={resolved}
      closeButton
      duration={3000}
      pauseWhenPageIsHidden
      // An open Radix dialog sets `pointer-events: none` on <body> so the page
      // behind it can't be clicked. The toaster is outside the dialog, so it
      // inherited that: hovering never paused the auto-close timer and the ×
      // couldn't be clicked. Opt the toast stack back in.
      style={{ '--width': '380px', pointerEvents: 'auto' } as CSSProperties}
      toastOptions={{
        classNames: {
          toast:
            '!items-start !gap-3 !rounded-2xl !border !border-clay-100 !bg-white !px-6 !py-4 !text-sm !font-medium !text-clay-600 !shadow-[0_10px_30px_-8px_rgba(15,23,42,0.18)] dark:!border-clay-600 dark:!bg-clay-700 dark:!text-clay-100',
          icon: '!mt-px !h-5 !w-5 [&_svg]:!h-5 [&_svg]:!w-5',
          title: '!text-sm !font-medium !leading-5 !text-clay-600 dark:!text-clay-100',
          description: '!mt-0.5 !text-xs !font-normal !text-clay-500 dark:!text-clay-300',
          // Round grey close button sitting on the card's top-left corner.
          closeButton:
            '!left-0 !top-0 !h-6 !w-6 !border-clay-300 !bg-clay-100 !text-clay-600 hover:!bg-clay-200 dark:!border-clay-500 dark:!bg-clay-600 dark:!text-clay-100 [&_svg]:!h-3.5 [&_svg]:!w-3.5',
          success: '[&_[data-icon]]:!text-success',
          error: '[&_[data-icon]]:!text-error',
          warning: '[&_[data-icon]]:!text-warning',
          info: '[&_[data-icon]]:!text-primary',
        },
      }}
    />
  )
}
